"use client";

import MarketPopup from "@app/_components/MarketSelector/MarketPopup";
import ReactQueryClientProvider from "@app/_components/ReactQueryClientProvider/ReactQueryClientProvider";
import LoginDialog from "@components/Auth/LoginDialog";
import { MarketSelector } from "@components/MarketSelector";
import { AuthProvider } from "@context/authContext";
import { SiteInfoProvider } from "@context/siteInfoContext";
import type { RootLayoutQuery } from "@server/gql/graphql";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useReportWebVitals } from "next/web-vitals";
import type { ReactNode } from "react";
import type { Dictionary } from "types";

import { envConfig } from "@/config/env";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import type { MarketConfiguration } from "@/config/markets/const";
import { enableClientDiagnostics } from "@/utils/diagnostics/enableClientDiagnostics";
import dynamic from "next/dynamic";
import type { PageMapEntry } from "@server/requests/contentful/getPageByType";

const CartProvider = dynamic(
  () => import("@context/cartContext").then((mod) => mod.CartProvider),
  { ssr: false },
);

type ProviderProps = {
  dictionary: Dictionary;
  language: string;
  locale: string | undefined;
  children: ReactNode;
  siteinfoCollection: RootLayoutQuery["siteinfoCollection"] | null;
  campaigns: RootLayoutQuery["campaigns"] | null;
  wishlist: RootLayoutQuery["wishlist"] | null;
  marketConfig: MarketConfiguration;
  pages?: Map<string, PageMapEntry>;
};

export default function Provider({
  dictionary,
  locale,
  children,
  siteinfoCollection,
  campaigns,
  wishlist,
  marketConfig,
  pages,
}: ProviderProps) {
  enableClientDiagnostics();

  if (envConfig.NEXT_PUBLIC_DATADOG_ENV === "dev") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useReportWebVitals((metric) => {
      console.log(metric);
    });
  }

  return (
    <ReactQueryClientProvider>
      <SiteInfoProvider
        siteinfoCollection={siteinfoCollection ?? undefined}
        campaigns={campaigns ?? undefined}
        wishlist={wishlist ?? undefined}
        marketConfig={marketConfig}
        pages={pages ?? undefined}
      >
        <AuthProvider locale={locale}>
          <CartProvider dictionary={dictionary}>
            {FEATURE_FLAGS.marketSelector.new ? (
              <MarketPopup dictionary={dictionary} />
            ) : (
              <MarketSelector dictionary={dictionary} />
            )}
            <LoginDialog />
            {children}
          </CartProvider>
        </AuthProvider>
      </SiteInfoProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </ReactQueryClientProvider>
  );
}
